////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// CORE-HELPERS.TS — shared, (near-)dependency-free front-end primitives.
//
// TS port of includes/js/core-helpers.js (Wave 2c of plans/bundler-script-load-order-phase2.md; Phase 1 of
// plans/template-js-carve-up.md). Public globals preserve the classic-script surface; icon type keys
// share their catalog with placeholder mounting and select rendering.
//
// ⚠ AUTO-ATTACH TRAP (why the export block grew): in the original CLASSIC <script>, every top-level
// `function`/`var` declaration AUTO-CREATED a window property, so the explicit `window.X =` list was
// INCOMPLETE — `iconUrl` (consumed by tom-select-init + others), `ftRuneTypeBadgeHtml`, `ftAbilityViewRerender`,
// and the `FT_ABILITY_*`/`_ftItemNameByIdMemo` vars relied on that implicit attach. A bundled IIFE module
// does NOT auto-attach, so this port explicitly attaches EVERY top-level declaration (functions + consts +
// vars) to window — a superset of the classic surface (over-attaching internals is harmless; missing one is
// a site-wide ReferenceError). tools/test/core-helpers-parity-smoke.mjs asserts every declared name reaches
// window.
//
// LOAD ORDER: loaded immediately AFTER the inline ICON_LOOKUP / *_CATALOG const block and BEFORE every JS
// bundle AND template.js. `iconMap` reads the inline `ICON_LOOKUP` catalog at call-time. Nothing here is
// invoked at module top level.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { ABILITY_BADGE_CANON as FT_ABILITY_BADGE_CANON,
    ABILITY_BADGE_ALIASES as FT_ABILITY_BADGE_ALIASES, abilityTypeForKey } from '../shared/icon-type-badges';

declare global {
    interface FtCoreCacheRoot {
        catalog?: Record<string, {
            item_name?: unknown;
            items?: { item_name?: unknown };
        }>;
    }
    interface FtCoreFateTide {
        casterCache?: Map<string, FtCoreCacheRoot>;
        objectCache?: Map<string, FtCoreCacheRoot>;
        encounterCache?: Map<string, FtCoreCacheRoot>;
        user?: { id?: unknown };
    }
    interface FtCoreRuneIcon {
        typeBadge(type: string, size: number): SVGElement;
        TYPE_LABEL?: Record<string, string>;
    }
    interface FtCoreBootstrapTooltip {
        dispose(): void;
    }
    interface FtCoreBootstrapHost {
        Tooltip?: {
            getInstance(element: Element): FtCoreBootstrapTooltip | null;
        };
    }
    interface FtCoreTomSelect {
        getValue(): unknown;
    }
    interface FtCoreValueElement extends HTMLElement {
        value: string;
        type?: string;
        checked?: boolean;
        multiple?: boolean;
        selectedOptions?: ArrayLike<{ value: unknown }>;
        tomselect?: FtCoreTomSelect;
    }
    interface FtCoreQuillEditor {
        root?: { innerHTML: string };
    }
    interface Window {
        _ftItemNameByIdMemo?: Map<string, string>;
        FateTide?: FtCoreFateTide;
        RuneIcon?: FtCoreRuneIcon | null;
        FT_ICON_REGISTRY?: Record<string, { file: string; tooltip: string }>;
        bootstrap?: FtCoreBootstrapHost;
        getActiveControllerCasterId?: () => unknown;
        ftGetMasterCasterId?: () => unknown;
        ftGetTargetSelection?: () => unknown;
        ftGetActiveEncounterId?: () => unknown;
        ftGetRollPrivate?: () => unknown;
        ftGetCampaignId?: () => unknown;
        ftGetLocationId?: () => unknown;
        ftGetCampaignSessionAttendance?: () => Record<string, unknown> | null;
        ftItemNameById?: typeof ftItemNameById;
        renderPcSheetPane?: (paneName: string, caster: unknown, casterId: string | null, pane: Element) => unknown;
        ftRerenderPcSheet?: (casterId: string | null) => unknown;
        ftRerenderCreatureAbilities?: (mount: Element) => unknown;
        ftLdTsRerender?: (mount: Element) => unknown;
    }
}


// Base URL for the site
// `${BASE_URL}options/game/encounter_creatures/go_to_encounter_inc.php`
// BASE_URL + "options/game/encounter_creatures/go_to_encounter_inc.php"
const BASE_URL = window.location.origin + '/';
const BASE_URL_CDN = "https://cdn.fatetide.com/";


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// UUID VALIDATION
function checkIfValidUUID(id: unknown): boolean {
    if (typeof id !== 'string' || !id) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// UUID → item_name resolver (session-global, path-independent). Walks the FateTide cache roots' `.catalog`
// (UUID → identity), tolerating BOTH the flat {item_name,item_type} and nested {items:{item_name}} shapes.
// Memoized — catalog identities are static. Defined in core-helpers (loaded before every bundle) so any
// renderer can resolve a bare-UUID reference to its name without threading the catalog through call sites.
// The rune-icon engine uses this to turn charge/contest UUID refs into the snake_cased icon filename
// (the CDN icons are name-keyed, so an unresolved UUID 404s). Returns '' on miss. Mirrors
// catalogNameById() in the PC-sheet bundle.
var _ftItemNameByIdMemo: Map<string, string> = window._ftItemNameByIdMemo instanceof Map
    ? window._ftItemNameByIdMemo as Map<string, string>
    : new Map<string, string>();
function ftItemNameById(id: string): string {
    if (typeof id !== 'string' || id === '') return '';
    const key = id.toUpperCase();
    const hit = _ftItemNameByIdMemo.get(key);
    if (hit !== undefined) return hit;
    const ft = window.FateTide;
    const caches = ft ? [ft.casterCache, ft.objectCache, ft.encounterCache] : [];
    for (const m of caches) {
        if (!m || typeof m.values !== 'function') continue;
        for (const root of m.values()) {
            const cat = root && root.catalog;
            if (!cat || typeof cat !== 'object') continue;
            const e = cat[id] || cat[key] || cat[id.toLowerCase()];
            if (!e || typeof e !== 'object') continue;
            const nm = (typeof e.item_name === 'string' && e.item_name !== '')
                ? e.item_name
                : (e.items && e.items.item_name);
            if (typeof nm === 'string' && nm !== '') { _ftItemNameByIdMemo.set(key, nm); return nm; }
        }
    }
    return '';
}
window.ftItemNameById = ftItemNameById;


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// HTML ESCAPING
function escapeHtml(str: unknown): string {
    return String(str).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[m] ?? m);
}

function escapeHtmlInventoryEnchantment(text: unknown): string {
    if (text == null) return '';
    const d = document.createElement('div');
    d.textContent = String(text);
    return d.innerHTML;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// TITLE-CASING
// Helper function: Convert underscores to spaces and capitalize each word
function ucwordsAndSpace(str: string): string {
    return str
        .replace(/_/g, ' ') // Replace underscores with spaces
        .split(' ')         // Split the string into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
        .join(' ');         // Join the words back into a string
}

/**
 * Title-case a string with proper handling (matches PHP ucwords_and_space exactly)
 * Replaces underscores with spaces, trims, lowercases first,
 * then capitalizes words except common lowercase words (unless first word).
 */
function ucWordsAndSpace(str: string | null | undefined, lowercaseWords: string[] = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'of', 'on', 'at', 'to', 'from', 'by', 'in', 'with']): string | undefined {
    if (str == null) {
        return undefined;
    }

    // Replace underscores with spaces, trim, and lowercase everything
    let cleaned = str.replace(/_/g, ' ').trim().toLowerCase();

    // Split into words (filter out empty strings from multiple spaces/underscores)
    let words = cleaned.split(/\s+/).filter(word => word.length > 0);

    if (words.length === 0) {
        return '';
    }

    // Capitalize the first word always
    const firstWord = words[0];
    if (firstWord === undefined) return '';
    words[0] = firstWord.charAt(0).toUpperCase() + firstWord.slice(1);

    // Process the rest: capitalize unless in lowercaseWords list
    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        if (word !== undefined && !lowercaseWords.includes(word)) {
            words[i] = word.charAt(0).toUpperCase() + word.slice(1);
        }
        // else leave it lowercase
    }

    return words.join(' ');
}


// → canonical ability item_type for any ability-type icon key (canon or plural page/nav form), or '' .
function ftAbilityBadgeType(key: unknown): string {
    return abilityTypeForKey(key);
}
// Render the rune type-badge as an HTML string (the string twin of buildTypeBadge in icon/index.ts).
function ftRuneTypeBadgeHtml(type: string, rawKey: string, w: number, h: number, title: string | boolean): string {
    const wi = parseInt(String(w), 10) || 25;
    const hi = parseInt(String(h), 10) || 25;
    const pop = title !== false;
    const customLabel = (typeof title === 'string' && title !== '') ? title : '';
    const RI = (typeof window !== 'undefined') ? window.RuneIcon : null;
    if (RI && typeof RI.typeBadge === 'function') {
        try {
            const svg = RI.typeBadge(type, Math.max(wi, hi));
            svg.setAttribute('width', String(wi));
            svg.setAttribute('height', String(hi));
            const svgHtml = svg.outerHTML;
            if (svgHtml) {
                if (!pop) return svgHtml;
                const label = customLabel || (RI.TYPE_LABEL && RI.TYPE_LABEL[type]) || type;
                const escaped = String(label)
                    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                return '<span class="d-inline-block ft-icon-map" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="' + escaped + '">' + svgHtml + '</span>';
            }
        } catch (e) { /* fall through to placeholder */ }
    }
    let attrs = " data-icon='" + rawKey + "' data-w='" + wi + "' data-h='" + hi + "'";
    if (!pop) attrs += " data-pop='0'";
    else if (customLabel) attrs += " data-title='" + customLabel.replace(/'/g, '&#39;') + "'";
    return "<i class='ft-icon'" + attrs + " style='display:inline-block;width:" + wi + "px;height:" + hi + "px' aria-hidden='true'></i>";
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ICON RENDERING
/**
 * Render an icon with optional tooltip – exact replica of the PHP icon_map()
 */
function iconMap(
    rawId: string,
    w = 25,
    h = 25,
    title: string | boolean = '',
    changePopupText?: string | null,
): string {
    if (!rawId) rawId = 'help-circle-outline';

    // Preserve the four-argument browser shorthand while also honoring the original PHP-compatible
    // fifth argument: iconMap(key, width, height, showPopup, customTooltip).
    const effectiveTitle = title === false
        ? false
        : (typeof changePopupText === 'string' && changePopupText !== '' ? changePopupText : title);

    // Ability-TYPE keys → unified rune type-badge (parity with icon_ph()/ftMountIcons), not a flat PNG.
    const badgeType = ftAbilityBadgeType(rawId);
    if (badgeType) {
        return ftRuneTypeBadgeHtml(badgeType, rawId, w, h, effectiveTitle);
    }

    // Resolve entry: handles UUID, name, or direct filename.
    const REG = (typeof window !== 'undefined' && window.FT_ICON_REGISTRY)
        || (typeof ICON_LOOKUP !== 'undefined' ? ICON_LOOKUP : {});
    let entry = REG[rawId];
    if (!entry && typeof rawId === 'string' && rawId.endsWith('s')) {
        const singular = rawId.replace(/s+$/, ''); // rtrim-equivalent: strip all trailing s's
        if (singular && singular !== rawId) {
            entry = REG[singular];
        }
    }
    if (!entry) {
        entry = {
            file: 'help-circle-outline',
            tooltip: ucWordsAndSpace(rawId) || 'Unknown'
        };
    }

    const id = entry.file;  // Actual filename for the <img src>
    let tooltipText = effectiveTitle;

    // Use resolved tooltip if no custom title provided
    if (effectiveTitle === '' || effectiveTitle === true) {
        tooltipText = entry.tooltip;
    }

    // Defensive: ensure tooltipText is always a string
    if (typeof tooltipText !== 'string') {
        tooltipText = String(tooltipText || 'Unknown');
    }

    // `alt` is an HTML ATTRIBUTE and must be escaped exactly like the tooltip below — it previously
    // interpolated tooltipText raw. Callers pass user-authored item/ability names here, and since the
    // PC-sheet entity-decode sweep those arrive as true plain text rather than pre-encoded, so an
    // unescaped " would break out of the attribute. Hoisted above `img` so BOTH sinks share one value
    // (the early `effectiveTitle === false` return below skips the tooltip, never the alt).
    const escaped = tooltipText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const src = `https://cdn.fatetide.com/images/icons/${id}.png`;
    const img = `<img src="${src}" width="${w}" height="${h}" alt="${escaped}" loading="lazy" decoding="async"
                     onerror="this.src='https://cdn.fatetide.com/images/icons/help-circle-outline.png'; this.onerror=null;">`;

    if (effectiveTitle === false) return img;

    // When rawId is a UUID, stamp data-ft-info-item-id so setupSheetAbilityPressForInfo can
    // openItemInfo on long-press (~450ms). Mirrors PHP icon_map().
    const isUuid = checkIfValidUUID(rawId);
    const dataAttr = isUuid ? ` data-ft-info-item-id="${rawId}"` : '';

    return `<span class="d-inline-block ft-icon-map" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${escaped}"${dataAttr}>${img}</span>`;
}

/**
 * iconUrl — resolve a key (variable_name / registered UUID / file name) to its bare CDN .png URL,
 * client-side, from window.FT_ICON_REGISTRY. Same registry + rtrim('s') singular fallback +
 * help-circle-outline default as iconMap().
 */
function iconUrl(rawId: string): string {
    const CDN = 'https://cdn.fatetide.com/images/icons/';
    if (!rawId) return CDN + 'help-circle-outline.png';
    const REG = (typeof window !== 'undefined' && window.FT_ICON_REGISTRY)
        || (typeof ICON_LOOKUP !== 'undefined' ? ICON_LOOKUP : {});
    let entry = REG[rawId];
    if (!entry && typeof rawId === 'string' && rawId.endsWith('s')) {
        const singular = rawId.replace(/s+$/, '');
        if (singular && singular !== rawId) entry = REG[singular];
    }
    return CDN + (entry ? entry.file : 'help-circle-outline') + '.png';
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// COOKIES
function getCookie(name: string): string | null {
    const cookieArr = document.cookie.split(';');
    for (const cookie of cookieArr) {
        const [key, value] = cookie.split('=').map(str => str.trim());
        if (key === name) return decodeURIComponent(value ?? '');
    }
    return null;
}

function setCookie(name: string, value: string, days?: number): void {
    var expires = "";
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=None; Secure";
}

function deleteCookie(name: string, path?: string, domain?: string): void {
    if (path === undefined) path = "/";
    document.cookie = name + "=" +
        "; expires=Thu, 01 Jan 1970 00:00:01 GMT" +
        ((path) ? "; path=" + path : "") +
        ((domain) ? "; domain=" + domain : "") +
        "; SameSite=None; Secure";
}

// SEE IF COOKIE IS SET
function isCookieSet(cookieName: string): boolean {
  var cookies = document.cookie.split(';');

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i]?.trim() ?? '';
    if (cookie.indexOf(cookieName + '=') === 0) {
      return true;
    }
  }

  return false;
}

// GET COOKIE VALUE
function getCookieValue(cookieName: string): string | null {
  var cookies = document.cookie.split(';');

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i]?.trim() ?? '';
    if (cookie.indexOf(cookieName + '=') === 0) {
      return cookie.substring(cookieName.length + 1);
    }
  }

  return null;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ELEMENT / FORM VALUE READERS (Phase 2a) — generic value-extraction primitives used app-wide to build AJAX
// payloads. getElementValue touches Quill / tomselect only at call-time (both load before this script).

// WAY TO HANDLE JSON SELECT DATA AND CHECKBOXES
function getElementValue(elementId: string): unknown {

	if (!elementId) return null; // Handle empty string, null, undefined

	// caster_id / caster_main_id: the hidden #caster_id / #caster_main_id inputs were deleted
	// (sidelog-hidden-input deprecation, 2026-06-22) — the cache is now the source of truth
	// (window.FateTide.activeCasterId / masterCasterId, read via getActiveControllerCasterId /
	// ftGetMasterCasterId). The ~20 server-rendered queryDataInputs buttons + direct callers still
	// ask for these by id, so resolve them from the cache here, in ONE place. Honour any surviving
	// element value first (transition safety), then fall back to the cache when missing/empty.
	if (elementId === 'caster_id' || elementId === 'caster_main_id') {
		const casterEl = document.getElementById(elementId) as FtCoreValueElement | null;
		const rawCaster = casterEl && typeof casterEl.value === 'string' ? casterEl.value.trim() : '';
		if (rawCaster !== '') return rawCaster;
		if (elementId === 'caster_id') {
			return (typeof window !== 'undefined' && window.getActiveControllerCasterId)
				? (window.getActiveControllerCasterId() || '') : '';
		}
		return (typeof window !== 'undefined' && window.ftGetMasterCasterId)
			? (window.ftGetMasterCasterId() || '') : '';
	}

	// target_id: selection state is window.FateTide.targetSelection via ftGetTargetSelection().
	// Generic form readers still ask for this legacy id, so resolve it from helper state here.
	if (elementId === 'target_id') {
		if (typeof window !== 'undefined' && typeof window.ftGetTargetSelection === 'function') {
			return window.ftGetTargetSelection() || '';
		}
	}

	// encounter_id: active encounter state is window.FateTide.activeEncounterId / encounter cache,
	// read via ftGetActiveEncounterId(). Many queryDataInputs callers still ask for this legacy id.
	if (elementId === 'encounter_id') {
		if (typeof window !== 'undefined' && typeof window.ftGetActiveEncounterId === 'function') {
			return window.ftGetActiveEncounterId() || '';
		}
	}

	// roll_private: roll visibility is cached on window.FateTide.rollPrivate via ftGetRollPrivate().
	if (elementId === 'roll_private') {
		if (typeof window !== 'undefined' && typeof window.ftGetRollPrivate === 'function') {
			return window.ftGetRollPrivate();
		}
	}

	// campaign_id: campaign state is window.FateTide.campaign/campaignArr/currentCampaignArr/cache,
	// read via ftGetCampaignId(). Many queryDataInputs callers still ask for this legacy id.
	if (elementId === 'campaign_id') {
		if (typeof window !== 'undefined' && typeof window.ftGetCampaignId === 'function') {
			return window.ftGetCampaignId() || '';
		}
	}

	// location_id: page/cached location context is resolved by ftGetLocationId().
	if (elementId === 'location_id') {
		if (typeof window !== 'undefined' && typeof window.ftGetLocationId === 'function') {
			return window.ftGetLocationId() || '';
		}
	}

	// user_id: authenticated viewer identity is bootstrapped into window.FateTide.user.
	if (elementId === 'user_id') {
		return (typeof window !== 'undefined' && window.FateTide?.user?.id)
			? String(window.FateTide.user.id)
			: '';
	}

	if (
		elementId === 'caster_active_in_campaign_session'
		|| elementId === 'next_campaign_session_id'
		|| elementId === 'next_campaign_session_start_time'
		|| elementId === 'next_campaign_session_end_time'
	) {
		if (typeof window !== 'undefined' && typeof window.ftGetCampaignSessionAttendance === 'function') {
			const session = window.ftGetCampaignSessionAttendance() || {};
			return session[elementId] || '';
		}
	}

	const element = document.getElementById(elementId) as FtCoreValueElement | null;

    if (!element) return null; // Return null if element does not exist

    // Quill-backed fields: read live HTML from the editor so AJAX payloads match the editor (debounced textarea sync can lag).
    if (typeof Quill !== 'undefined') {
        const quillHost =
            document.getElementById('quill_' + elementId) ||
            document.querySelector('.quill-editor[data-layer-type-name="' + String(elementId).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]');
        if (quillHost) {
            const quillApi: { find(host: Element): unknown } = Quill;
            const quill = quillApi.find(quillHost) as FtCoreQuillEditor | null;
            if (quill?.root && element && (element.tagName === 'TEXTAREA' || element.tagName === 'INPUT')) {
                element.value = quill.root.innerHTML;
            }
        }
    }

    if (element.tagName === "SELECT" && element.tomselect) {
        const ts = element.tomselect;
        const v = ts.getValue();
        if (element.multiple) {
            if (Array.isArray(v)) return v.filter(Boolean);
            if (v === '' || v === null || v === undefined) return [];
            return [v];
        }
        return v ?? '';
    }

    if (element.tagName === "SELECT" && element.multiple && element.selectedOptions) {
        // If the element is a select with multiple attribute
        return Array.from(element.selectedOptions).map(option => option.value);
    } else if (element.type === "checkbox") {
        // Match getCheckboxValue(): unchecked boolean form controls submit as 0, not blank.
        return element.checked ? element.value : 0;
    } else {
        // For other input types (including single select)
        return element.value;
    }
}

 function isEmpty(value: unknown): boolean {
    return (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
    );
}

function getCheckboxValue<T = number>(checkboxId: string, defaultValue: T = 0 as T): string | T {
    const checkboxElement = document.getElementById(checkboxId) as FtCoreValueElement | null;
    if (checkboxElement && checkboxElement.checked) {
        return checkboxElement.value;
    }
    return defaultValue;
}


function getCheckboxElementValue<T = number>(checkboxElement: HTMLInputElement | null | undefined, defaultValue: T = 0 as T): string | T {
    if (checkboxElement && checkboxElement.checked) {
        return checkboxElement.value;
    }
    return defaultValue;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ABILITY VIEW MODE — shared list↔icon display toggle for the ability sections (PC-sheet Abilities tab AND
// creature list-display). Both bundles consume these as window globals; the choice is cookied (sheet-wide).
var FT_ABILITY_VIEW_MODE_COOKIE = 'ft_ability_view_mode'; // 'list' (default) | 'icon'
var FT_ABILITY_ICON_SIZE_COOKIE = 'ft_ability_icon_size'; // 's' | 'm' (default) | 'l'

function ftGetAbilityViewMode() {
    return getCookie(FT_ABILITY_VIEW_MODE_COOKIE) === 'icon' ? 'icon' : 'list';
}
function ftGetAbilityIconSize() {
    var s = getCookie(FT_ABILITY_ICON_SIZE_COOKIE);
    return (s === 's' || s === 'l') ? s : 'm';
}
// Icon size → rune LOD px (renderer derives detail tier: <72 → L0, 72-95 → L1, >=96 → L2).
function ftAbilityIconSizeToRunePx(size: 's' | 'm' | 'l'): number {
    return size === 's' ? 48 : (size === 'l' ? 96 : 72);
}

// Wrap a set of pre-built medallion HTML strings (or one joined string) in the icon-strip container.
function ftAbilityIconStripWrap(inner: string | string[], size?: 's' | 'm' | 'l'): string {
    var html = Array.isArray(inner) ? inner.join('') : (inner || '');
    return '<div class="ft-ability-iconstrip ft-ai--' + (size || ftGetAbilityIconSize()) + '">' + html + '</div>';
}

// The global toolbar shown above the ability area: [List | Icons] segmented control + an S/M/L size group.
function ftAbilityViewToolbarHtml() {
    var mode = ftGetAbilityViewMode();
    var size = ftGetAbilityIconSize();
    var listActive = mode === 'list' ? ' active' : '';
    var listBtn = '<button type="button" class="btn btn-sm ft-abv-mode' + listActive + '"'
        + ' data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="List view" aria-label="List view"'
        + ' onclick="ftSetAbilityViewMode(\'list\', this)"><span aria-hidden="true">☰</span></button>';
    var sizeBtn = function (s: 's' | 'm' | 'l', label: string, tip: string): string {
        var active = (mode === 'icon' && size === s) ? ' active' : '';
        return '<button type="button" class="btn btn-sm ft-abv-size' + active + '"'
            + ' data-ft-abv-size="' + s + '" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="' + tip + '" aria-label="' + tip + '"'
            + ' onclick="ftSetAbilityIconSize(\'' + s + '\', this)">' + label + '</button>';
    };
    return '<div class="ft-ability-view-toolbar d-flex flex-wrap align-items-center gap-2 mb-2" role="group" aria-label="Ability display">'
        + '<div class="btn-group btn-group-sm" role="group">' + listBtn + sizeBtn('s', 'S', 'Small icons') + sizeBtn('m', 'M', 'Medium icons') + sizeBtn('l', 'L', 'Large icons') + '</div>'
        + '</div>';
}

// Fill every (unfilled) toolbar host with the current toolbar markup.
function ftMountAbilityViewToolbars(root?: ParentNode | null, force = false): void {
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var hosts = scope.querySelectorAll('.ft-ability-view-toolbar-host');
    for (var i = 0; i < hosts.length; i++) {
        const host = hosts[i];
        if (!host) continue;
        if (!force && host.getAttribute('data-ft-abv-filled') === '1') continue;
        // Dispose the current buttons' tooltips before replacing them.
        const Tooltip = window.bootstrap?.Tooltip;
        if (Tooltip) {
            host.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(function (t: Element) {
                var inst = Tooltip.getInstance(t);
                if (inst) { inst.dispose(); }
            });
        }
        host.innerHTML = ftAbilityViewToolbarHtml();
        host.setAttribute('data-ft-abv-filled', '1');
    }
}

function ftAbilityViewRerender(el: Element | null | undefined): void {
    if (!el || typeof el.closest !== 'function') return;
    var root = el.closest('.fate-tide-sheet-abilities-search-root');
    if (root) {
        var pane = root.querySelector('.ft-pc-sheet-pane[data-ft-pc-pane="ability_sections"][data-ft-caster-id]');
        if (pane) {
            var cid = pane.getAttribute('data-ft-caster-id');
            var paneName = pane.getAttribute('data-ft-pc-pane');
            var cache = window.FateTide && window.FateTide.casterCache;
            var caster = (cache && cid) ? cache.get(cid) : null;
            if (caster && paneName && typeof window.renderPcSheetPane === 'function') {
                window.renderPcSheetPane(paneName, caster, cid, pane);
            } else if (cid && typeof window.ftRerenderPcSheet === 'function') {
                window.ftRerenderPcSheet(cid);
            }
        }
        ftMountAbilityViewToolbars(root, true);
        return;
    }
    var mount = el.closest('.ft-ld-ts-list');
    if (mount) {
        if (typeof window.ftRerenderCreatureAbilities === 'function') window.ftRerenderCreatureAbilities(mount);
        else if (typeof window.ftLdTsRerender === 'function') window.ftLdTsRerender(mount);
        return;
    }
    var pane2 = el.closest('.ft-pc-sheet-pane[data-ft-caster-id]');
    if (pane2 && typeof window.ftRerenderPcSheet === 'function') window.ftRerenderPcSheet(pane2.getAttribute('data-ft-caster-id'));
}
function ftSetAbilityViewMode(mode: string, el: Element | null): void {
    setCookie(FT_ABILITY_VIEW_MODE_COOKIE, mode === 'icon' ? 'icon' : 'list', 365);
    ftAbilityViewRerender(el);
}
// Picking a size IS choosing icon mode — set BOTH cookies, then re-render ONCE.
function ftSetAbilityIconSize(size: string, el: Element | null): void {
    setCookie(FT_ABILITY_ICON_SIZE_COOKIE, (size === 's' || size === 'l') ? size : 'm', 365);
    setCookie(FT_ABILITY_VIEW_MODE_COOKIE, 'icon', 365);
    ftAbilityViewRerender(el);
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// WINDOW EXPORTS — EXHAUSTIVE (every top-level declaration). The classic <script> auto-attached every
// top-level function/var to window; a module does not, so we attach them ALL explicitly here (a superset of
// the old surface — the bare-`iconUrl`/`ftRuneTypeBadgeHtml`/`ftAbilityViewRerender` and the FT_ABILITY_*
// vars that the old explicit list silently relied on auto-attach for). Verified by core-helpers-parity-smoke.
Object.assign(window, {
    // consts
    BASE_URL, BASE_URL_CDN, FT_ABILITY_BADGE_CANON, FT_ABILITY_BADGE_ALIASES,
    // vars
    _ftItemNameByIdMemo, FT_ABILITY_VIEW_MODE_COOKIE, FT_ABILITY_ICON_SIZE_COOKIE,
    // functions
    checkIfValidUUID, ftItemNameById, escapeHtml, escapeHtmlInventoryEnchantment,
    ucwordsAndSpace, ucWordsAndSpace, ftAbilityBadgeType, ftRuneTypeBadgeHtml, iconMap, iconUrl,
    getCookie, setCookie, deleteCookie, isCookieSet, getCookieValue,
    getElementValue, isEmpty, getCheckboxValue, getCheckboxElementValue,
    ftGetAbilityViewMode, ftGetAbilityIconSize, ftAbilityIconSizeToRunePx, ftAbilityIconStripWrap,
    ftAbilityViewToolbarHtml, ftMountAbilityViewToolbars, ftAbilityViewRerender,
    ftSetAbilityViewMode, ftSetAbilityIconSize,
});

export {};
